import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
const MIN_PASSWORD_LENGTH = 8;
export default function AcceptAdminInvite() {
    const navigate = useNavigate();
    const [ready, setReady] = useState(false);
    const [fullName, setFullName] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [invalidLink, setInvalidLink] = useState(false);
    useEffect(() => {
        let done = false;
        function resolve(session) {
            if (done)
                return;
            done = true;
            if (session?.user)
                setReady(true);
            else
                setInvalidLink(true);
        }
        // PKCE flow — Supabase redirects with ?code= in the URL
        const code = new URLSearchParams(window.location.search).get('code');
        if (code) {
            supabase.auth.exchangeCodeForSession(code)
                .then(({ data }) => resolve(data.session))
                .catch(() => resolve(null));
            return;
        }
        // Implicit / hash flow — client auto-processes the hash and fires SIGNED_IN
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (session?.user) {
                resolve(session);
                return;
            }
            const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
                if (event === 'SIGNED_IN' && session?.user)
                    resolve(session);
            });
            const timer = setTimeout(() => resolve(null), 6000);
            // Cleanup stored on the effect closure so it runs on unmount
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            cleanup = () => { subscription.unsubscribe(); clearTimeout(timer); };
        });
        let cleanup = () => { };
        return () => cleanup();
    }, []);
    async function handleSetPassword(e) {
        e.preventDefault();
        setError(null);
        if (password.length < MIN_PASSWORD_LENGTH) {
            setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
            return;
        }
        if (password !== confirm) {
            setError('Passwords do not match.');
            return;
        }
        if (!fullName.trim()) {
            setError('Please enter your name.');
            return;
        }
        setLoading(true);
        const { error: updateError } = await supabase.auth.updateUser({ password });
        if (updateError) {
            setLoading(false);
            setError(updateError.message);
            return;
        }
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
            await supabase.from('profiles').update({ full_name: fullName.trim() }).eq('id', user.id);
        }
        setLoading(false);
        navigate('/admin', { replace: true });
    }
    if (invalidLink) {
        return (_jsx("div", { className: "auth-page", children: _jsxs("div", { className: "auth-card", children: [_jsx("div", { className: "flex justify-center mb-6", children: _jsx("img", { src: "/images/logo-v2.webp", alt: "Springfield Dog Centre", className: "w-56" }) }), _jsx("h1", { children: "Link expired or invalid" }), _jsx("p", { children: "This invite link has already been used or has expired. Ask an admin to send a new invite." }), _jsx("p", { children: _jsx(Link, { to: "/login", children: "Back to log in" }) })] }) }));
    }
    if (!ready) {
        return (_jsx("div", { className: "auth-page", children: _jsxs("div", { className: "auth-card", children: [_jsx("div", { className: "flex justify-center mb-6", children: _jsx("img", { src: "/images/logo-v2.webp", alt: "Springfield Dog Centre", className: "w-56" }) }), _jsx("p", { className: "text-center text-sm text-gray-500", children: "Verifying your invite link\u2026" })] }) }));
    }
    return (_jsx("div", { className: "auth-page", children: _jsxs("div", { className: "auth-card", children: [_jsx("div", { className: "flex justify-center mb-6", children: _jsx("img", { src: "/images/logo-v2.webp", alt: "Springfield Dog Centre", className: "w-56" }) }), _jsx("h1", { children: "Set your password" }), _jsx("p", { children: "Welcome to the Springfield Dog Centre admin portal. Set a password to complete your account." }), _jsxs("form", { onSubmit: handleSetPassword, noValidate: true, children: [_jsxs("div", { className: "field", children: [_jsx("label", { htmlFor: "full_name", children: "Your name" }), _jsx("input", { id: "full_name", type: "text", autoComplete: "name", required: true, value: fullName, onChange: (e) => setFullName(e.target.value), placeholder: "First and last name" })] }), _jsxs("div", { className: "field", children: [_jsx("label", { htmlFor: "password", children: "Password" }), _jsx("input", { id: "password", type: "password", autoComplete: "new-password", required: true, value: password, onChange: (e) => setPassword(e.target.value), placeholder: `At least ${MIN_PASSWORD_LENGTH} characters` })] }), _jsxs("div", { className: "field", children: [_jsx("label", { htmlFor: "confirm", children: "Confirm password" }), _jsx("input", { id: "confirm", type: "password", autoComplete: "new-password", required: true, value: confirm, onChange: (e) => setConfirm(e.target.value) })] }), error && _jsx("p", { className: "error", children: error }), _jsx("button", { type: "submit", disabled: loading, children: loading ? 'Saving…' : 'Set password & go to admin' })] })] }) }));
}
